import menuMessages from '@/messages/menu.ts';
import { FileRoutesByTo } from '../../routeTree.gen.ts';
import {
  Bell,
  Building2,
  ClipboardList,
  LayoutGrid,
  type LucideIcon,
  ScrollText,
  Shield,
  Stethoscope,
  Users,
} from 'lucide-react';

export type TRoutes = keyof FileRoutesByTo;

export type TRoutesWithLabels = { title: string; href: TRoutes }[];

// Admin
export type TAdminRoutes = {
  home: TRoutes;
  assessments: TRoutes;
  eula: TRoutes;
  organizations: TRoutes;
  organizationLeads: TRoutes;
  memberCare: TRoutes;
  providers: TRoutes;
  providersCreate: TRoutes;
  contracts: TRoutes;
  notifications: TRoutes;
  organizationAccess: TRoutes;
  featuresManagement: TRoutes;
  metrics: TRoutes;
  creatorApplications: TRoutes;
  contentModeration: TRoutes;
  brandedContent: TRoutes;
};

export const adminRoutes: TAdminRoutes = {
  home: '/',
  assessments: '/assessments',
  eula: '/eula',
  organizations: '/organizations',
  organizationLeads: '/organization-leads',
  memberCare: '/member-care',
  providers: '/providers',
  providersCreate: '/providers/create',
  contracts: '/contracts',
  notifications: '/notifications',
  organizationAccess: '/super-admin/organization-access',
  featuresManagement: '/super-admin/features-management',
  metrics: '/super-admin/metrics',
  creatorApplications: '/creator-applications',
  contentModeration: '/content/moderation',
  brandedContent: '/content/branded-content',
};

// A sidebar entry is either a direct link (leaf) or an expandable group of links.
// `icon` is set on groups and top-level leaves; nested sub-items stay icon-less
// and rely on the group's tree-guide line for hierarchy.
export type TMenuLeaf = { title: string; href: TRoutes; icon?: LucideIcon };
export type TMenuGroup = { title: string; icon: LucideIcon; children: TMenuLeaf[] };
export type TMenuEntry = TMenuLeaf | TMenuGroup;

export const isMenuGroup = (entry: TMenuEntry): entry is TMenuGroup =>
  Object.prototype.hasOwnProperty.call(entry, 'children');

export const adminMenu: TMenuEntry[] = [
  {
    title: 'Users',
    icon: Users,
    children: [
      { title: 'Regular Users', href: adminRoutes.home },
      { title: 'Member Coordinators', href: adminRoutes.memberCare },
    ],
  },
  {
    title: 'Organizations',
    icon: Building2,
    children: [
      { title: 'List', href: adminRoutes.organizations },
      { title: 'Contracts', href: adminRoutes.contracts },
      { title: 'Leads', href: adminRoutes.organizationLeads },
    ],
  },
  { title: 'Providers', href: adminRoutes.providers, icon: Stethoscope },
  {
    title: 'Content',
    icon: LayoutGrid,
    children: [
      { title: 'Creator Applications', href: adminRoutes.creatorApplications },
      { title: 'Moderation', href: adminRoutes.contentModeration },
      { title: 'Branded Content', href: adminRoutes.brandedContent },
    ],
  },
  { title: 'Notifications', href: adminRoutes.notifications, icon: Bell },
  { title: 'EULA', href: adminRoutes.eula, icon: ScrollText },
  { title: 'Assessments (deprecated)', href: adminRoutes.assessments, icon: ClipboardList },
  {
    title: 'Super Admin',
    icon: Shield,
    children: [
      { title: 'Organization Access', href: adminRoutes.organizationAccess },
      { title: 'Features Management', href: adminRoutes.featuresManagement },
      { title: 'Metrics', href: adminRoutes.metrics },
    ],
  },
];

// Member Support
export type TMemberCareRoutes = {
  memberCareMembers: TRoutes;
  memberCareProfile: TRoutes;
  memberCareProfileContact: TRoutes;
  memberCareProfileBio: TRoutes;
}; // memberCareDashboard: TRoutes;

export const memberCareRoutes: TMemberCareRoutes = {
  // memberCareDashboard: '/member-care/dashboard',
  memberCareMembers: '/member-care/members',
  memberCareProfile: '/member-care/profile',
  memberCareProfileContact: '/member-care/profile/contact',
  memberCareProfileBio: '/member-care/profile/bio',
};

export const memberCareRoutesWithLabels: TRoutesWithLabels = [
  // { title: 'M. Care Dashboard', href: memberCareRoutes.memberCareDashboard },
  { title: menuMessages.myMembers, href: memberCareRoutes.memberCareMembers },
];

export const nonAuthRoutes: { signIn: TRoutes } = {
  signIn: '/sign-in',
};

const appRoutes = {
  ...adminRoutes,
  ...memberCareRoutes,
  ...nonAuthRoutes,
};

export default appRoutes;
